import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUsers, FiHeart, FiBriefcase, FiHome, FiAward, FiTrendingUp } = FiIcons;

const ImpactCounter = () => {
  const [counts, setCounts] = useState({
    lives: 0,
    meals: 0,
    jobs: 0,
    shelters: 0,
    training: 0,
    success: 0
  });

  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  const targets = {
    lives: 2547,
    meals: 125000,
    jobs: 892,
    shelters: 15,
    training: 1250,
    success: 95
  };

  const impactData = [
    {
      icon: FiUsers,
      key: 'lives',
      label: 'Lives Rescued & Rehabilitated',
      suffix: '+',
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      icon: FiHeart,
      key: 'meals',
      label: 'Nutritious Meals Served',
      suffix: '+',
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-red-50',
      textColor: 'text-red-600'
    },
    {
      icon: FiBriefcase,
      key: 'jobs',
      label: 'Sustainable Jobs Created',
      suffix: '+',
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    {
      icon: FiHome,
      key: 'shelters',
      label: 'Shelter Homes Operational',
      suffix: '',
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    },
    {
      icon: FiAward,
      key: 'training',
      label: 'People Skill Trained',
      suffix: '+',
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600'
    },
    {
      icon: FiTrendingUp,
      key: 'success',
      label: 'Success Rate',
      suffix: '%',
      color: 'from-emerald-500 to-emerald-600',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-600'
    }
  ];

  useEffect(() => {
    if (inView) {
      const duration = 2500;
      const steps = 100;
      const stepTime = duration / steps;

      const interval = setInterval(() => {
        setCounts(prevCounts => {
          const newCounts = { ...prevCounts };
          let allComplete = true;

          Object.keys(targets).forEach(key => {
            if (newCounts[key] < targets[key]) {
              const increment = Math.ceil(targets[key] / steps);
              newCounts[key] = Math.min(newCounts[key] + increment, targets[key]);
              allComplete = false;
            }
          });

          if (allComplete) {
            clearInterval(interval);
          }

          return newCounts;
        });
      }, stepTime);

      return () => clearInterval(interval);
    }
  }, [inView]);

  return (
    <section ref={ref} className="py-24 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-20 w-72 h-72 bg-orange-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="typography-caption text-orange-600 mb-4">Our Measurable Impact</div>
          <h2 className="typography-heading text-4xl md:text-5xl text-gray-900 mb-6">
            Transforming Lives,
            <span className="block text-transparent bg-clip-text gradient-premium">
              Creating Futures
            </span>
          </h2>
          <p className="typography-body text-xl text-gray-600 max-w-3xl mx-auto">
            Every number represents a real person whose life has been transformed through our comprehensive rehabilitation programs. These aren't just statistics – they're stories of hope, dignity, and new beginnings.
          </p>
        </motion.div>

        {/* Impact Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {impactData.map((item, index) => (
            <motion.div
              key={item.key}
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5, transition: { duration: 0.2 } }}
              className="card-premium p-8 text-center group"
            >
              {/* Icon */}
              <div className="relative mb-6">
                <div className={`w-20 h-20 ${item.bgColor} rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300`}>
                  <SafeIcon icon={item.icon} className={`w-10 h-10 ${item.textColor}`} />
                </div>
                <div className={`absolute -inset-2 bg-gradient-to-r ${item.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur`}></div>
              </div>

              {/* Counter */}
              <div className="mb-4">
                <motion.div
                  className="text-4xl md:text-5xl font-bold text-gray-900 mb-2"
                  initial={{ scale: 0.5 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  {counts[item.key].toLocaleString()}{item.suffix}
                </motion.div>
              </div>

              {/* Label */}
              <h3 className="typography-body text-lg font-semibold text-gray-700 mb-3">
                {item.label}
              </h3>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <motion.div
                  className={`h-full bg-gradient-to-r ${item.color} rounded-full`}
                  initial={{ width: 0 }}
                  whileInView={{ width: `${(counts[item.key] / targets[item.key]) * 100}%` }}
                  transition={{ duration: 1.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                ></motion.div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="text-center mt-20"
        >
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-premium border border-gray-100 max-w-4xl mx-auto">
            <h3 className="typography-heading text-2xl md:text-3xl text-gray-900 mb-4">
              Be Part of This Impact
            </h3>
            <p className="typography-body text-gray-600 mb-8 max-w-2xl mx-auto">
              Join thousands of compassionate individuals and organizations who are making a real difference in transforming lives across India.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-premium-primary"
              >
                Start Your Impact
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-premium-secondary"
              >
                View Detailed Report
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ImpactCounter;